import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@lyder-talk/core";
import { AuthRequiredModal } from "./AuthRequiredModal";

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * The *route* guard: a whole page needs an account.
 *
 * A different job from `useAuthRequired()`, which gates one action mid-task and
 * resumes it. Here there is nothing to resume, so the page itself stays mounted
 * — visible but inert behind the modal — and the visitor can see what they
 * would get before deciding.
 *
 * This is the safety net, not the front door. In-app navigation is declined
 * before it happens by `useGuardedNavigate`, so clicking "My Topics" normally
 * never reaches here. What lands here is a *direct* load: a pasted URL, a
 * bookmark, a link from outside the app — arrivals that cannot be intercepted,
 * where rendering the page behind the gate is the best available answer.
 *
 * It renders the same `AuthRequiredModal` as every other gate rather than an
 * auth UI of its own. It used to mount `AuthModal`, a second, complete sign-in
 * / sign-up / forgot-password implementation (725 lines) with its own copy,
 * its own Supabase calls and its own Google handling, which drifted from the
 * forms on `/auth/*` one string at a time. Two answers to "how do I sign in"
 * is one too many; the modal and the standalone pages now share everything.
 */
export const RequireAuth = ({ children }: RequireAuthProps) => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (isLoading) {
    return <>{children}</>;
  }

  if (!isAuthenticated) {
    return (
      <>
        <div className="pointer-events-none select-none">
          {children}
        </div>
        <AuthRequiredModal
          open
          onOpenChange={(open) => {
            /*
             * Dismissing has to go somewhere. The page behind is inert, so
             * staying put would leave the visitor staring at a dead screen with
             * nothing to click. Home is public and is the one destination that
             * cannot itself be gated. `replace` so Back does not walk straight
             * back into the gate.
             */
            if (!open) navigate("/", { replace: true });
          }}
          title={t("auth.modal.required.title", "Sign in to continue")}
          description={t(
            "auth.modal.required.guardDescription",
            "This page is saved to your account. Sign in or create one to open it.",
          )}
        />
      </>
    );
  }

  return <>{children}</>;
};
